import styled, { css } from 'styled-components';
import { Button, ButtonIcon, FieldWrapper, Tag } from 'components';
import Select from 'components/Select/Select';
import { lineHeight, theme, toRem } from 'design';

export const TaskHeader = styled.div`
  display: flex;
  flex-flow: row wrap;
  justify-content: space-between;
  align-items: center;
  width: 100%;
  gap: ${toRem(theme.space.md)};
  margin-top: ${toRem(theme.space.xl)};
  padding-bottom: ${toRem(theme.space.lg)};
  margin-bottom: ${toRem(theme.space.lg)};
  border-bottom: ${toRem(1)} solid ${theme.color.neutral[200]};
`;

export const TaskTitles = styled.div`
  display: flex;
  flex-flow: column nowrap;
  gap: ${toRem(theme.space.xs)};
`;

export const TaskTitle = styled.h1`
  font-size: ${toRem(theme.font.size['3xl'])};
  font-weight: ${theme.font.weight.semiBold};
  line-height: ${lineHeight(theme.font.size['3xl'])};
  color: ${theme.color.neutral[900]};
`;

export const TaskSubtitle = styled.p`
  font-size: ${toRem(theme.font.size.md)};
  line-height: ${lineHeight(theme.font.size.md)};
  color: ${theme.color.neutral[600]};
`;

// Figma sets the "·" separators in subtitles apart with extra space on both sides.
export const Separator = styled.span`
  margin: 0 ${toRem(theme.space.xs)};
`;

/*
 * The shared Button only comes in 44px ("large") and 33px ("small"). The labor review design uses two
 * more compact sizes — 32px for toolbar-level actions and 24px for inline row actions — so they're
 * sized here, scoped to this screen, rather than adding sizes to the shared component.
 */
const compactButtonStyles = (height: number, paddingX: number) => `
  height: ${toRem(height)};
  padding: 0 ${toRem(paddingX)};
  border-radius: ${toRem(height / 2)};
  font-size: ${toRem(theme.font.size.xs)};
  white-space: nowrap;
`;

export const MediumButton = styled(Button)`
  ${compactButtonStyles(32, theme.space.lg)}
`;

export const RowButton = styled(Button)`
  ${compactButtonStyles(24, theme.space.md)}
`;

export const LoadingContainer = styled.div`
  display: flex;
  flex-flow: column nowrap;
  justify-content: center;
  align-items: center;
  width: 100%;
  height: ${toRem(300)};
  gap: ${toRem(theme.space.sm)};
`;

export const ToolbarRow = styled.div`
  display: flex;
  flex-flow: row wrap;
  justify-content: space-between;
  align-items: center;
  width: 100%;
  gap: ${toRem(theme.space.md)};
  margin-bottom: ${toRem(theme.space.lg)};
`;

export const ToolbarSearch = styled.div`
  flex: 1 1 ${toRem(280)};

  @media (min-width: ${theme.breakpoint.small}px) {
    max-width: ${toRem(360)};
  }
`;

// Below the small breakpoint the toolbar stacks: full-width search, then the switch and date tag spread across one row, then the count.
export const ToolbarRight = styled.div`
  display: flex;
  flex-flow: row wrap;
  justify-content: flex-start;
  align-items: center;
  gap: ${toRem(theme.space.md)};
  width: 100%;

  @media (min-width: ${theme.breakpoint.small}px) {
    width: auto;
  }
`;

export const ToolbarFilters = styled.div`
  display: flex;
  flex-flow: row nowrap;
  justify-content: space-between;
  align-items: center;
  gap: ${toRem(theme.space.md)};
  width: 100%;

  @media (min-width: ${theme.breakpoint.small}px) {
    width: auto;
  }
`;

export const ToolbarSwitch = styled.div`
  display: flex;
  flex-flow: row nowrap;
  align-items: center;
  gap: ${toRem(theme.space.xs)};
`;

export const ToolbarSwitchLabel = styled.span`
  font-size: ${toRem(theme.font.size.md)};
  color: ${theme.color.neutral[900]};
  white-space: nowrap;
`;

const tagStyles = `
  padding: 0 ${toRem(theme.space.sm)};
  border-radius: ${toRem(4)};
  white-space: nowrap;
`;

export const ToolbarDateTag = styled(Tag)`
  ${tagStyles}
  color: ${theme.color.neutral[800]};
`;

export const ToolbarCount = styled.span`
  font-size: ${toRem(theme.font.size.md)};
  font-weight: ${theme.font.weight.medium};
  color: ${theme.color.neutral[600]};
  white-space: nowrap;
`;

export const Notice = styled.div`
  display: flex;
  flex-flow: column nowrap;
  width: 100%;
  gap: ${toRem(theme.space.xs)};
  padding: ${toRem(theme.space.md)} ${toRem(theme.space.lg)};
  border-radius: ${toRem(8)};
  background-color: ${theme.color.warning[25]};
  margin-bottom: ${toRem(theme.space.lg)};
`;

export const NoticeTitle = styled.p`
  font-size: ${toRem(theme.font.size.md)};
  font-weight: ${theme.font.weight.semiBold};
  line-height: ${lineHeight(theme.font.size.md)};
  color: ${theme.color.neutral[900]};
`;

export const NoticeMessage = styled.p`
  font-size: ${toRem(theme.font.size.md)};
  line-height: ${lineHeight(theme.font.size.md)};
  color: ${theme.color.neutral[800]};
`;

export const EmployeeListLayout = styled.div`
  display: flex;
  flex-flow: column nowrap;
  gap: ${toRem(theme.space.md)};
  width: 100%;
`;

export const EmployeeCardLayout = styled.div`
  display: flex;
  flex-flow: column nowrap;
  gap: ${toRem(theme.space.md)};
  width: 100%;
  padding: ${toRem(theme.space.lg)};
  border: ${toRem(1)} solid ${theme.color.neutral[200]};
  border-radius: ${toRem(12)};
  background-color: ${theme.color.white};
`;

// Below the small breakpoint the actions drop under the employee's name, left-aligned.
export const EmployeeCardHeader = styled.div`
  display: flex;
  flex-flow: column nowrap;
  align-items: flex-start;
  gap: ${toRem(theme.space.md)};
  width: 100%;

  @media (min-width: ${theme.breakpoint.small}px) {
    flex-flow: row wrap;
    justify-content: space-between;
    align-items: center;
    gap: ${toRem(theme.space.sm)};
  }
`;

export const EmployeeIdentity = styled.div`
  display: flex;
  flex-flow: row nowrap;
  align-items: center;
  gap: ${toRem(theme.space.md)};
`;

export const EmployeeName = styled.div`
  display: flex;
  flex-flow: column nowrap;
  gap: ${toRem(theme.space['2xs'])};
`;

export const EmployeeNameText = styled.span`
  font-size: ${toRem(theme.font.size.lg)};
  font-weight: ${theme.font.weight.semiBold};
  line-height: ${lineHeight(theme.font.size.lg)};
  color: ${theme.color.neutral[900]};
`;

export const EmployeeSubtext = styled.span`
  font-size: ${toRem(theme.font.size.xxs)};
  line-height: ${lineHeight(theme.font.size.xxs)};
  color: ${theme.color.neutral[600]};
  white-space: nowrap;
`;

// Stacks the status (tags, duration) above the buttons below the small breakpoint, and lines them up in one row above it.
export const EmployeeCardActions = styled.div`
  display: flex;
  flex-flow: column nowrap;
  align-items: flex-start;
  gap: ${toRem(theme.space.sm)};

  @media (min-width: ${theme.breakpoint.small}px) {
    flex-flow: row wrap;
    align-items: center;
    margin-left: auto;
  }
`;

export const EmployeeCardActionGroup = styled.div`
  display: flex;
  flex-flow: row wrap;
  align-items: center;
  gap: ${toRem(theme.space.sm)};
`;

export const EmployeeDuration = styled.span`
  font-size: ${toRem(theme.font.size.md)};
  font-weight: ${theme.font.weight.semiBold};
  color: ${theme.color.neutral[900]};
  white-space: nowrap;
`;

export const ManualOverrideTag = styled(Tag)`
  ${tagStyles}
  color: ${theme.color.warning[100]};
`;

export const ManualOverrideTooltip = styled.div`
  display: flex;
  flex-flow: column nowrap;
  gap: ${toRem(theme.space.xs)};
  max-width: ${toRem(240)};
  /* The tooltip renders inside the tag, which is nowrap — let the reason wrap so it stays inside the box */
  white-space: normal;
  overflow-wrap: anywhere;
`;

export const ReviewTag = styled(Tag)`
  ${tagStyles}
  color: ${theme.color.danger[50]};
`;

export const EmployeeEmptyState = styled.div`
  display: flex;
  flex-flow: row wrap;
  justify-content: space-between;
  align-items: center;
  gap: ${toRem(theme.space.sm)};
  padding: ${toRem(theme.space.md)};
  border-radius: ${toRem(8)};
  background-color: ${theme.color.neutral[50]};
  width: 100%;
`;

export const EmployeeEmptyMessage = styled.span`
  font-size: ${toRem(theme.font.size.md)};
  line-height: ${lineHeight(theme.font.size.md)};
  color: ${theme.color.neutral[600]};
`;

export const ShiftsLayout = styled.div`
  display: flex;
  flex-flow: column nowrap;
  gap: ${toRem(theme.space.sm)};
  width: 100%;
`;

/*
 * Columns follow the design's widths (Job, Clock in, Clock out, Breaks, Duration) as maximums, so the
 * columns line up across every card and shrink together on narrower screens instead of wrapping. Below
 * the small breakpoint each column becomes a line of its own, label on the left and value on the right.
 */
export const ShiftRowLayout = styled.div`
  display: grid;
  grid-template-columns: minmax(0, 1fr);
  align-items: start;
  gap: ${toRem(theme.space.lg)};
  padding: ${toRem(theme.space.md)};
  border-radius: ${toRem(8)};
  background-color: ${theme.color.neutral[50]};
  width: 100%;

  @media (min-width: ${theme.breakpoint.small}px) {
    gap: ${toRem(theme.space.md)} ${toRem(theme.space.lg)};
    grid-template-columns: minmax(0, ${toRem(360)}) minmax(0, ${toRem(180)}) minmax(0, ${toRem(180)}) minmax(0, ${toRem(260)}) minmax(0, ${toRem(150)});
  }
`;

export const ShiftRowCol = styled.div<{ align?: 'left' | 'right' }>`
  display: flex;
  flex-flow: row nowrap;
  justify-content: space-between;
  align-items: center;
  gap: ${toRem(theme.space.xs)};
  text-align: right;
  min-width: 0;

  @media (min-width: ${theme.breakpoint.small}px) {
    flex-flow: column nowrap;
    justify-content: flex-start;
    align-items: ${({ align }) => (align === 'right' ? 'flex-end' : 'flex-start')};
    text-align: ${({ align }) => (align === 'right' ? 'right' : 'left')};
  }
`;

export const ShiftRowLabel = styled.span`
  flex-shrink: 0;
  font-size: ${toRem(theme.font.size.xs)};
  font-weight: ${theme.font.weight.semiBold};
  line-height: ${lineHeight(theme.font.size.xs)};
  color: ${theme.color.neutral[600]};
`;

export const ShiftRowValue = styled.span`
  font-size: ${toRem(theme.font.size.md)};
  line-height: ${lineHeight(theme.font.size.md)};
  color: ${theme.color.neutral[900]};
`;

export const BoardEmptyState = styled.div`
  display: flex;
  flex-flow: column nowrap;
  justify-content: center;
  align-items: center;
  gap: ${toRem(theme.space.sm)};
  width: 100%;
  padding: ${toRem(theme.space['3xl'])} ${toRem(theme.space.lg)};
  border: ${toRem(1)} solid ${theme.color.neutral[200]};
  border-radius: ${toRem(8)};
  background-color: ${theme.color.white};

  svg {
    width: ${toRem(48)};
    height: ${toRem(48)};
    fill: ${theme.color.neutral[400]};
  }
`;

// Takes the place of the whole board, so it keeps the board header's distance from the top of the page.
export const LoadErrorState = styled(BoardEmptyState)`
  margin-top: ${toRem(theme.space.xl)};
`;

export const BoardEmptyMessage = styled.p`
  font-size: ${toRem(theme.font.size.md)};
  line-height: ${lineHeight(theme.font.size.md)};
  color: ${theme.color.neutral[600]};
  text-align: center;
`;

export const ModalSubtitle = styled.p`
  font-size: ${toRem(theme.font.size.md)};
  line-height: ${lineHeight(theme.font.size.md)};
  color: ${theme.color.neutral[800]};
  text-align: center;
  margin-top: ${toRem(theme.space.sm)};
  margin-bottom: ${toRem(theme.space.xl)};
`;

/*
 * The shift form stacks everything (shift cards, "Add another shift", the reason field) with one
 * uniform gap, and each shift card stacks its own rows the same way. FieldWrapper's own bottom margin
 * is cleared inside both so the gap is the only source of spacing.
 */
export const ShiftFormLayout = styled.div`
  display: flex;
  flex-flow: column nowrap;
  gap: ${toRem(theme.space.md)};
  width: 100%;
  margin-bottom: ${toRem(theme.space.xl)};

  > div {
    margin-bottom: 0;
  }
`;

export const EditShiftLayout = styled.div`
  display: flex;
  flex-flow: column nowrap;
  gap: ${toRem(theme.space.md)};
  width: 100%;
  padding: ${toRem(theme.space.md)};
  background-color: ${theme.color.neutral[50]};
  border-radius: ${toRem(8)};

  > div {
    margin-bottom: 0;
  }
`;

export const AddShiftButton = styled(MediumButton)`
  align-self: flex-start;
`;

export const EditShiftHeader = styled.div`
  display: flex;
  flex-flow: row nowrap;
  justify-content: space-between;
  align-items: center;
`;

export const ShiftHeading = styled.span`
  font-size: ${toRem(theme.font.size.md)};
  font-weight: ${theme.font.weight.semiBold};
  line-height: ${lineHeight(theme.font.size.md)};
  color: ${theme.color.neutral[900]};
`;

/* off-scale exception: the remove button's width/height, not spacing */
const REMOVE_BUTTON_SIZE = 24;

export const RemoveButton = styled(ButtonIcon)`
  width: ${toRem(REMOVE_BUTTON_SIZE)};
  height: ${toRem(REMOVE_BUTTON_SIZE)};
  border-color: ${theme.color.neutral[300]};

  svg,
  &:hover svg {
    width: ${toRem(16)};
    fill: ${theme.color.neutral[500]};
  }
`;

/*
 * The base Select rather than JobTitleSelect, which reads (and fetches into) the site's shared job titles: a shift
 * only offers this screen's POS job titles. It's rendered in a modal, so the open dropdown layers above the modal.
 */
export const ShiftJobTitleSelect = styled(Select)`
  .react-dropdown-select-dropdown {
    z-index: ${theme.zIndex.dialog};
  }
`;

/*
 * TimeMaskedInput has no error state of its own, so the red border comes from the field wrapping it.
 * Also clears FieldWrapper's bottom margin: rows here are spaced by their parent's gap.
 */
export const TimeField = styled(FieldWrapper)`
  margin-bottom: 0;

  ${({ hasError }) =>
    hasError &&
    css`
      input {
        border-color: ${theme.color.danger[50]};
      }
    `}
`;

/*
 * Shared by ShiftFieldsRow and BreakRow so "Break start" always lines up with "Clock in" and the break
 * end (plus its remove button) with "Clock out".
 */
const shiftRowColumns = `repeat(2, minmax(0, 1fr))`;

export const ShiftFieldsRow = styled.div`
  display: grid;
  grid-template-columns: ${shiftRowColumns};
  align-items: start;
  gap: ${toRem(theme.space.md)};
`;

export const BreakRow = styled(ShiftFieldsRow)`
  width: 100%;
`;

/*
 * Aligned from the top (not centered) so a validation message under the field can't push the remove
 * button out of line with the input. Its margin re-centers it against the input itself: the label row
 * above the input (font size + its bottom margin) plus half the input/button height difference.
 */
export const BreakEndCell = styled.div`
  display: flex;
  flex-flow: row nowrap;
  align-items: flex-start;
  gap: ${toRem(theme.space.md)};
  min-width: 0;

  > button {
    margin-top: ${toRem(theme.font.size.sm + theme.space.sm + (theme.input.height.large - REMOVE_BUTTON_SIZE) / 2)};
  }

  // The input shares this column with the remove button, so let its validation message run on under the button, wrapping at the column's edge.
  [data-cy='feedback-message'] {
    margin-right: -${toRem(theme.space.md + REMOVE_BUTTON_SIZE)};
  }
`;

/*
 * Spacing between break rows, and between the last break row and "Add break", comes entirely from
 * this gap so a shift with zero breaks doesn't add it a second time on top of EditShiftLayout's own gap.
 */
export const BreaksSection = styled.div`
  display: flex;
  flex-flow: column nowrap;
  align-items: flex-start;
  gap: ${toRem(theme.space.md)};
`;

export const AddBreakButton = styled(RowButton)``;

/*
 * The design marks required reasons with a red asterisk after the label; FieldWrapper only takes a
 * plain-string label, so it's drawn here.
 */
export const RequiredField = styled(FieldWrapper)`
  [data-cy='label'] > span:first-child::after {
    content: '*';
    margin-left: ${toRem(theme.space['2xs'])};
    color: ${theme.color.danger[50]};
  }
`;

export const OverrideReasonBox = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 100%;
  gap: ${toRem(theme.space.xs)};
  padding: ${toRem(theme.space.md)} ${toRem(theme.space.lg)};
  border-radius: ${toRem(8)};
  background-color: ${theme.color.info[25]};
  margin-bottom: ${toRem(theme.space.md)};
`;

export const OverrideReasonLabel = styled.p`
  font-size: ${toRem(theme.font.size.md)};
  font-weight: ${theme.font.weight.semiBold};
  line-height: ${lineHeight(theme.font.size.md)};
  color: ${theme.color.neutral[900]};
  text-align: center;
`;

export const OverrideReasonText = styled.p`
  font-size: ${toRem(theme.font.size.md)};
  line-height: ${lineHeight(theme.font.size.md)};
  color: ${theme.color.neutral[800]};
  text-align: center;
`;

export const OverrideReasonBy = styled.p`
  font-size: ${toRem(theme.font.size.xs)};
  color: ${theme.color.neutral[600]};
  text-align: center;
`;

const MODAL_BUTTON_WIDTH = 178;

/*
 * Two buttons at MODAL_BUTTON_WIDTH don't fit a phone-width modal, so below the small breakpoint they
 * stack full width, the primary action on top.
 */
export const ModalButtonRow = styled.div`
  display: flex;
  flex-flow: column-reverse nowrap;
  gap: ${toRem(theme.space.md)};
  width: 100%;

  > button {
    width: 100%;
  }

  @media (min-width: ${theme.breakpoint.small}px) {
    flex-flow: row nowrap;
    justify-content: center;
    gap: ${toRem(theme.space.lg)};

    > button {
      width: auto;
      min-width: ${toRem(MODAL_BUTTON_WIDTH)};
    }
  }
`;

// Same button width for the AlertContent-based modals, whose footer buttons are rendered by AlertContent.
export const alertFooterButtonStyles = css`
  @media (min-width: ${theme.breakpoint.small}px) {
    [data-cy='modal-footer'] button {
      min-width: ${toRem(MODAL_BUTTON_WIDTH)};
    }
  }
`;

export const OverrideWarningList = styled.ul`
  display: flex;
  flex-flow: column nowrap;
  gap: ${toRem(theme.space.md)};
  width: 100%;
  padding-left: ${toRem(theme.space.lg)};
  list-style-type: disc;
`;

export const OverrideWarningItem = styled.li`
  font-size: ${toRem(theme.font.size.md)};
  line-height: ${lineHeight(theme.font.size.md)};
  color: ${theme.color.neutral[800]};
`;
